import { getPostPaywall } from '@wix/ambassador-blog-blog-paywall-service-v1-post-paywall/http';
import type { RichContent } from '@wix/ricos';
import type { IHttpClient } from '@wix/yoshi-flow-editor';
import type { ErrorHandlerPublicAPI } from '@wix/yoshi-flow-editor/build/cjs/exports/error-handler';
import { appendAuthHeaders } from '../controller/helpers';
import { type PlatformApi } from '../controller/platform-api';

/**
 * blog-paywall-service keys a paywall by the fqdn of the entity that owns the content, so a post
 * being previewed from the composer has to be looked up as a draft: it has no published
 * counterpart yet, and once it does the two are told apart by this fqdn alone — they share an id.
 */
const DRAFT_POST_FQDN = 'wix.blog.v4.draft_post';

type FetchPaywallContentParams = {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  platformApi: PlatformApi;
  instance?: string;
};

/**
 * Paywall content for a draft post, resolved by blog-paywall-service: the draft's own content,
 * the PaywallTemplate it points at, or the site's default template for its language.
 */
export const fetchDraftPostPaywallContent = async (
  draftPostId: string,
  { httpClient, errorHandler, instance }: FetchPaywallContentParams,
): Promise<RichContent | undefined> => {
  const response = await errorHandler.withErrorHandler(
    () =>
      httpClient.request(
        getPostPaywall({ postFqdn: DRAFT_POST_FQDN, postId: draftPostId }),
        instance ? { signedInstance: instance } : undefined,
      ),
    { errorCodesMap: {} },
  );

  // blog-paywall-service and ricos generate nominally distinct rich-content types with identical
  // wire shapes — cast is safe, and keeps both fetchers returning what `paywallRichContent` takes.
  return response.data?.richContent as RichContent | undefined;
};

/**
 * Legacy paywall banner from paywall-server — one default banner per site and language, with no
 * knowledge of the post it is shown on.
 */
export const fetchDefaultPaywallBanner = async ({
  httpClient,
  errorHandler,
  platformApi,
  instance,
}: FetchPaywallContentParams): Promise<RichContent | undefined> => {
  const response = await errorHandler.withErrorHandler(
    () =>
      httpClient.get<{ banner: RichContent }>(
        `/_api/paywall-server/rich-content-banners/default?lang=${
          platformApi.site.language ?? 'en'
        }&site=${platformApi.location.baseUrl ? platformApi.location.baseUrl : '[site-url]'}`,
        {
          headers: appendAuthHeaders(platformApi, { instance }),
        },
      ),
    { errorCodesMap: {} },
  );

  return response.data?.banner;
};
